import { useMutation, useQueryClient } from "react-query"
import {
  createTicketPurchase,
  updateTicketPurchase,
  redeemTicketPurchase,
  unredeemTicketPurchase,
  deleteTicketPurchase,
  type TicketPurchaseCreateParams,
  type TicketPurchaseUpdateParams,
} from "../requests/ticketPurchases"
import { ticketPurchasesQueryKeys } from "./useTicketPurchases"
import { ticketPurchaseQueryKeys } from "./useTicketPurchase"

/**
 * Hook for creating ticket purchases
 */
export function useCreateTicketPurchase() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      bandId,
      params,
    }: {
      bandId: number
      params: TicketPurchaseCreateParams
    }) => createTicketPurchase(bandId, params),
    {
      onSuccess: (data) => {
        // Both create and update render the same `create.json.jbuilder`
        // shape — `ticket_purchases` (plural), never a singular `ticket_purchase`.
        const postId = data.ticket_purchases?.[0]?.post_id
        if (data.success && postId) {
          queryClient.invalidateQueries(
            ticketPurchasesQueryKeys.byPost(postId),
          )
          // Invalidate myTickets query so the button updates on the event page
          queryClient.invalidateQueries(
            ticketPurchaseQueryKeys.myTickets(postId),
          )
        }
      },
    },
  )
}

/**
 * Hook for updating ticket purchases
 */
export function useUpdateTicketPurchase() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      bandId,
      ticketPurchaseId,
      params,
    }: {
      bandId: number
      ticketPurchaseId: number
      params: TicketPurchaseUpdateParams
    }) => updateTicketPurchase(bandId, ticketPurchaseId, params),
    {
      onSuccess: (data) => {
        // Both create and update render the same `create.json.jbuilder`
        // shape — `ticket_purchases` (plural), never a singular `ticket_purchase`.
        const postId = data.ticket_purchases?.[0]?.post_id
        if (data.success && postId) {
          queryClient.invalidateQueries(
            ticketPurchasesQueryKeys.byPost(postId),
          )
          // Invalidate myTickets query so the button updates on the event page
          queryClient.invalidateQueries(
            ticketPurchaseQueryKeys.myTickets(postId),
          )
        }
      },
    },
  )
}

/**
 * Hook for redeeming ticket purchases
 */
export function useRedeemTicketPurchase() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      postId,
      ticketPurchaseId,
    }: {
      postId: number
      ticketPurchaseId: number
    }) => redeemTicketPurchase(postId, ticketPurchaseId),
    {
      onSuccess: (data, variables) => {
        // Invalidate ticket purchases queries for the post
        if (data.success) {
          queryClient.invalidateQueries(
            ticketPurchasesQueryKeys.byPost(variables.postId),
          )
          // Invalidate myTickets query so the button updates on the event page
          queryClient.invalidateQueries(
            ticketPurchaseQueryKeys.myTickets(variables.postId),
          )
        }
      },
    },
  )
}

/**
 * Hook for unredeeming ticket purchases
 */
export function useUnredeemTicketPurchase() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      postId,
      ticketPurchaseId,
    }: {
      postId: number
      ticketPurchaseId: number
    }) => unredeemTicketPurchase(postId, ticketPurchaseId),
    {
      onSuccess: (data, variables) => {
        // Invalidate ticket purchases queries for the post
        if (data.success) {
          queryClient.invalidateQueries(
            ticketPurchasesQueryKeys.byPost(variables.postId),
          )
          // Invalidate myTickets query so the button updates on the event page
          queryClient.invalidateQueries(
            ticketPurchaseQueryKeys.myTickets(variables.postId),
          )
        }
      },
    },
  )
}

/**
 * Hook for deleting a not-yet-redeemed guest list (comped) ticket
 */
export function useDeleteTicketPurchase() {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      postId,
      ticketPurchaseId,
    }: {
      postId: number
      ticketPurchaseId: number
    }) => deleteTicketPurchase(postId, ticketPurchaseId),
    {
      onSuccess: (data, variables) => {
        if (data.success) {
          queryClient.invalidateQueries(
            ticketPurchasesQueryKeys.byPost(variables.postId),
          )
          queryClient.invalidateQueries(
            ticketPurchaseQueryKeys.myTickets(variables.postId),
          )
        }
      },
    },
  )
}

/**
 * Combined hook for all ticket purchase mutations
 */
export function useTicketPurchaseMutations() {
  const createMutation = useCreateTicketPurchase()
  const updateMutation = useUpdateTicketPurchase()
  const redeemMutation = useRedeemTicketPurchase()
  const unredeemMutation = useUnredeemTicketPurchase()
  const deleteMutation = useDeleteTicketPurchase()

  return {
    create: createMutation,
    update: updateMutation,
    redeem: redeemMutation,
    unredeem: unredeemMutation,
    delete: deleteMutation,

    // Convenience methods
    isAnyLoading:
      createMutation.isLoading ||
      updateMutation.isLoading ||
      redeemMutation.isLoading ||
      unredeemMutation.isLoading ||
      deleteMutation.isLoading,

    hasAnyError:
      createMutation.isError ||
      updateMutation.isError ||
      redeemMutation.isError ||
      unredeemMutation.isError ||
      deleteMutation.isError,
  }
}
